/**
 * Abertura da sessão da suíte (SPEC-v2 2.2; ADR-009). Ordem:
 *   1. `?demo=<papel>` (só no dev server): sessão de demonstração sem senha;
 *   2. sessão guardada neste navegador (token da aba ou "Manter conectado");
 *   3. sem sessão: `/configurar` se ainda não há dono, senão `/entrar?volta=…`.
 */
import type { AuthProvider, Role, SessionInfo } from '@rota/contracts';
import { demoLoginFromUrl, demoRoleFromUrl } from '@/data/auth/demo';
import { sessionStore } from '@/data/auth/session';
import { getSuite } from '@/data/provider';

interface MockExtras {
  isConfigured?(): Promise<boolean>;
  demoSession?(role: Role, opts: { allow: boolean }): Promise<SessionInfo>;
}

let inflight: Promise<void> | null = null;

export function bootstrapSession(): Promise<void> {
  inflight ??= (async () => {
    sessionStore.set({ status: 'loading' });
    const auth = getSuite().auth as AuthProvider & MockExtras;
    try {
      if (demoLoginFromUrl() && auth.isConfigured && auth.setupOwner && !(await auth.isConfigured())) {
        // Dono de demonstração com senha aleatória que ninguém vê: a tela /entrar fica capturável.
        const secret = `${crypto.randomUUID()}${crypto.randomUUID()}`;
        await auth.setupOwner({ name: 'Lucas Almeida', email: 'lucas@exemplo.com.br', password: secret });
        await auth.signOut();
      }
      const demo = demoRoleFromUrl();
      if (demo && auth.demoSession) {
        sessionStore.signedIn(await auth.demoSession(demo, { allow: true }));
        return;
      }
      const s = await auth.session();
      if (s) {
        sessionStore.signedIn(s);
        return;
      }
      const configured = auth.isConfigured ? await auth.isConfigured() : true;
      sessionStore.signedOut(!configured);
    } catch (err) {
      console.warn('[Rota] não foi possível abrir a sessão', err);
      sessionStore.signedOut(false);
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/** Recarrega a sessão do provider (depois de trocar senha, nome, 2FA). */
export async function refreshSession(): Promise<void> {
  const s = await getSuite().auth.session();
  if (s) sessionStore.signedIn(s);
  else sessionStore.signedOut(false);
}
